import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowRight, Repeat, Target, Workflow } from "lucide-react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/contexts/AuthContext";
import type { Database } from "@/lib/supabase";

type FollowUpRow = Database["public"]["Tables"]["account_assistant_follow_ups"]["Row"];
type IntervalRow = Database["public"]["Tables"]["account_assistant_follow_up_intervals"]["Row"];

const unitLabel = (unit: string, value: number) => {
  const labels: Record<string, [string, string]> = {
    minutes: ["minuto", "minutos"],
    hours: ["hora", "horas"],
    days: ["dia", "dias"],
  };
  const [singular, plural] = labels[unit] ?? [unit, unit];
  return value === 1 ? singular : plural;
};

const FollowUpTab: React.FC = () => {
  const { id: assistantId } = useParams<{ id: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [followUps, setFollowUps] = useState<FollowUpRow[]>([]);
  const [intervalsByFollowUp, setIntervalsByFollowUp] = useState<Record<string, IntervalRow[]>>({});
  const [loading, setLoading] = useState(true);

  const loadFollowUps = useCallback(async () => {
    if (!assistantId || !supabase) return;
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("account_assistant_follow_ups")
        .select("*")
        .eq("assistant_id", assistantId)
        .order("created_at", { ascending: false });
      if (error) throw error;

      const rows = (data || []) as FollowUpRow[];
      setFollowUps(rows);

      if (rows.length > 0) {
        const { data: intervalsData, error: intervalsError } = await supabase
          .from("account_assistant_follow_up_intervals")
          .select("*")
          .in("follow_up_id", rows.map((item) => item.id))
          .order("sequence_order", { ascending: true });
        if (intervalsError) throw intervalsError;

        const grouped: Record<string, IntervalRow[]> = {};
        for (const interval of (intervalsData || []) as IntervalRow[]) {
          (grouped[interval.follow_up_id] = grouped[interval.follow_up_id] || []).push(interval);
        }
        setIntervalsByFollowUp(grouped);
      }
    } catch (error) {
      console.error("Erro ao carregar follow-ups do assistente:", error);
    } finally {
      setLoading(false);
    }
  }, [assistantId]);

  useEffect(() => {
    if (user?.account_id) loadFollowUps();
  }, [user?.account_id, loadFollowUps]);

  return (
    <div className="w-full flex pb-8 flex-col gap-4">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 rounded-xl border border-[#E5E7EB] bg-[#EEF4FF] dark:border-[#3F3F464D] dark:bg-blue-500/10">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-[#DBEAFE] flex items-center justify-center flex-shrink-0 dark:bg-blue-500/20">
            <Repeat size={18} className="text-[#2563EB] dark:text-[#60A5FA]" />
          </div>
          <div>
            <p className="font-semibold text-[#0F172A] dark:text-[#FAFAFA]">
              Os follow-ups agora têm uma área própria
            </p>
            <p className="text-sm text-[#475569] dark:text-[#A1A1AA] mt-0.5">
              Crie e edite sequências com objetivo, etapas de texto ou fluxo, regras de parada e
              monitor de contatos em cadência.
            </p>
          </div>
        </div>
        <Button onClick={() => navigate("/follow-ups")} className="flex items-center gap-2 flex-shrink-0">
          Gerenciar Follow-ups
          <ArrowRight size={16} />
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-8 text-[#64748B] bg-[#F8FAFC] rounded-xl border border-[#E5E7EB] dark:text-[#71717A] dark:bg-[#27272A]/30 dark:border-[#3F3F464D]">
          Carregando follow-ups...
        </div>
      ) : followUps.length > 0 ? (
        <div className="w-full flex flex-col gap-3">
          <h3 className="text-lg font-semibold text-[#0F172A] dark:text-[#FAFAFA]">
            Sequências deste assistente
          </h3>
          {followUps.map((followUp) => {
            const intervals = intervalsByFollowUp[followUp.id] || [];
            return (
              <button
                key={followUp.id}
                onClick={() => navigate(`/follow-ups/editar/${followUp.id}`)}
                className="flex items-start justify-between gap-3 p-4 rounded-xl border border-[#E5E7EB] bg-white shadow-sm text-left hover:border-[#2563EB]/40 transition-colors dark:border-[#3F3F464D] dark:bg-[#27272A] dark:shadow-none dark:hover:border-[#60A5FA]/40"
              >
                <div className="flex flex-col gap-2">
                  <div className="flex items-center gap-2">
                    <span className="text-[#0F172A] dark:text-[#FAFAFA] font-medium">{followUp.name}</span>
                    <span
                      className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${
                        followUp.is_active
                          ? "bg-green-100 text-green-700 dark:bg-green-500/15 dark:text-green-400"
                          : "bg-slate-100 text-slate-500 dark:bg-neutral-500/15 dark:text-neutral-400"
                      }`}
                    >
                      {followUp.is_active ? "Ativa" : "Inativa"}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {intervals.map((interval, index) => (
                      <span
                        key={interval.id}
                        className="flex items-center gap-1 px-2 py-0.5 rounded text-xs bg-[#DBEAFE] text-[#2563EB] dark:bg-blue-500/20 dark:text-[#60A5FA]"
                      >
                        {interval.fluxo_id && <Workflow size={11} />}
                        {index + 1}ª · {interval.delay_value} {unitLabel(interval.delay_unit, interval.delay_value)}
                      </span>
                    ))}
                  </div>
                  {(followUp.goal_pipeline_stage_id || (followUp.goal_tags || []).length > 0) && (
                    <span className="flex w-fit items-center gap-1 px-2 py-0.5 rounded-full bg-green-100 text-green-700 text-[11px] font-medium dark:bg-green-500/15 dark:text-green-400">
                      <Target size={11} />
                      {followUp.goal_pipeline_stage_id
                        ? "Meta: etapa do funil"
                        : `Meta: tag ${(followUp.goal_tags || []).join(", ")}`}
                    </span>
                  )}
                </div>
                <ArrowRight size={16} className="text-[#94A3B8] dark:text-[#71717A] mt-1 flex-shrink-0" />
              </button>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-8 text-[#64748B] bg-[#F8FAFC] rounded-xl border border-[#E5E7EB] dark:text-[#71717A] dark:bg-[#27272A]/30 dark:border-[#3F3F464D]">
          <p>Nenhuma sequência de follow-up para este assistente</p>
          <p className="text-sm mt-2">Crie a primeira na página Follow-ups</p>
        </div>
      )}
    </div>
  );
};

export default FollowUpTab;
